import React, { useEffect, useState } from "react";
import { useClients } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlatformBadge, ModeBadge, PLATFORM_LABEL } from "@/components/Badges";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plug, Plus, CheckCircle2, AlertCircle, Clock, Link2, ShieldAlert } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const TOKEN_STYLE = {
  "gültig": { cls: "text-emerald-600", icon: CheckCircle2, label: "Token gültig" },
  "nicht_verbunden": { cls: "text-slate-400", icon: AlertCircle, label: "Nicht verbunden" },
  "abgelaufen": { cls: "text-rose-600", icon: Clock, label: "Abgelaufen" },
};

const CREDENTIAL_LABEL = {
  standard: "Comrocket Standard",
  tenant: "Mandanteneigene App",
  environment: "Server-Standard",
};

const tokenExpired = (expiresAt) => Boolean(expiresAt && new Date(expiresAt).getTime() <= Date.now());

export default function Accounts() {
  const { t, locale } = useLanguage();
  const { current } = useClients();
  const { can, user } = useAuth();
  const canConnect = (platform) => can("admin") || (user?.review_access?.meta_actions && !user?.review_access?.read_only && ["facebook", "instagram"].includes(platform));
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_id: "", platform: "tiktok", handle: "", mode: "simulation" });
  const [providers, setProviders] = useState(null);
  const [credsDialog, setCredsDialog] = useState(null);
  const [credsForm, setCredsForm] = useState({ app_client_id: "", app_client_secret: "" });
  const [savedCreds, setSavedCreds] = useState({});
  const [connections, setConnections] = useState([]);

  const load = () => { if (current) api.get(`/accounts?client_id=${current.id}`).then(({ data }) => setAccounts(data)); };
  const loadProviders = () => { if (current) api.get(`/social/providers?client_id=${current.id}`).then(({ data }) => setProviders(data)).catch(() => {}); };
  const loadCredentials = () => {
    if (!current || !can("admin")) return;
    api.get(`/social/credentials?client_id=${current.id}`).then(({ data }) => {
      setSavedCreds(Object.fromEntries(data.map((item) => [item.platform, item])));
    }).catch(() => {});
  };
  const loadConnections = () => { if (current) api.get(`/social/tokens?client_id=${current.id}`).then(({ data }) => setConnections(data)).catch(() => {}); };
  useEffect(() => {
    load();
    loadProviders();
    loadCredentials();
    loadConnections();
    if (current) api.get(`/projects?client_id=${current.id}`).then(({ data }) => { setProjects(data); if (data[0]) setForm((f) => ({ ...f, project_id: data[0].id })); });
  }, [current]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("social_connected")) { toast.success(`${p.get("social_connected")} verbunden`); window.history.replaceState({}, "", "/accounts"); load(); loadConnections(); }
    else if (p.get("social_error")) { toast.error(`OAuth-Fehler: ${p.get("social_error")}`); window.history.replaceState({}, "", "/accounts"); }
  }, []);

  const oauthMap = { tiktok: "tiktok", instagram: "instagram", facebook: "facebook", linkedin: "linkedin", youtube: "youtube" };
  const providerFor = (platform) => providers?.providers?.find((p) => p.key === oauthMap[platform]);

  const oauthConnect = async (platform, accountId) => {
    const prov = oauthMap[platform];
    try {
      const { data } = await api.post(`/social/${prov}/connect`, { client_id: current.id, account_id: accountId });
      window.location.href = data.auth_url;
    } catch (e) { toast.error(apiError(e)); }
  };

  const oauthConnectProvider = async (providerKey) => {
    try {
      const { data } = await api.post(`/social/${providerKey}/connect`, { client_id: current.id });
      window.location.href = data.auth_url;
    } catch (e) { toast.error(apiError(e)); }
  };

  const openCreds = (key) => {
    setCredsForm({ app_client_id: savedCreds[key]?.app_client_id || "", app_client_secret: "" });
    setCredsDialog(key);
  };
  const selectPage = async (page_id, platform) => {
    try { await api.post("/social/select-page", { client_id: current.id, platform, page_id }); toast.success(t("Seite ausgewählt")); loadConnections(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const assignConnection = async (project_id, platform) => {
    try {
      const { data } = await api.post("/social/assign-account", { client_id: current.id, platform, project_id });
      toast.success(`${platform}-Profil zugeordnet · Modus: ${data.mode === "simulation" ? "Simulation" : data.mode}`);
      load(); loadConnections();
    } catch (e) { toast.error(apiError(e)); }
  };
  const disconnectConnection = async (platform) => {
    if (!window.confirm(`${platform}-Verbindung wirklich trennen?`)) return;
    try { await api.delete(`/social/${platform}/token?client_id=${current.id}`); toast.success(`${platform} getrennt`); loadConnections(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveCreds = async () => {
    if (!credsForm.app_client_id.trim()) { toast.error("Client ID / App ID erforderlich"); return; }
    if (!credsForm.app_client_secret.trim() && !savedCreds[credsDialog]?.secret_configured) { toast.error("Client Secret / App Secret erforderlich"); return; }
    try {
      await api.put(`/social/${credsDialog}/credentials`, { client_id: current.id, ...credsForm });
      toast.success(t("Credentials gespeichert (verschlüsselt)"));
      setCredsDialog(null); loadProviders(); loadCredentials();
    } catch (e) { toast.error(apiError(e)); }
  };

  const connect = async (id) => { try { await api.post(`/accounts/${id}/connect`); toast.success("Verbunden (simuliert)"); load(); } catch (e) { toast.error(apiError(e)); } };
  const setMode = async (id, mode) => { await api.patch(`/accounts/${id}?mode=${mode}`); load(); };
  const create = async () => {
    try { await api.post("/accounts", { ...form, client_id: current.id }); toast.success(t("Account angelegt")); setOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // LinkedIn uses one OAuth token per tenant. Older project mappings may still
  // exist for historical posts, but presenting each mapping as a separate
  // connection is misleading. Keep those records intact and show only the
  // account currently bound to the active LinkedIn token.
  const activeLinkedInAccountId = connections.find((c) => c.platform === "linkedin")?.account_id;
  const visibleAccounts = accounts.filter(
    (account) => account.platform !== "linkedin" || !activeLinkedInAccountId || account.id === activeLinkedInAccountId,
  );

  if (!current) return <div className="text-slate-400">{t("Kein Mandant ausgewählt.")}</div>;

  return (
    <div>
      <PageHeader overline={current.name} title="Social Accounts" subtitle={t("OAuth-Verbindungen, Token-Status & Betriebsmodus pro Account. Echte Plattform-OAuth folgt (benötigt App-Credentials).")}
        actions={can("admin") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="account-add-btn" className="rounded-full bg-[#0A0A0A] hover:bg-[#212529] font-semibold"><Plus size={15} className="mr-1.5" /> {t("Account hinzufügen")}</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">{t("Social Account hinzufügen")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">{t("Projekt")}</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}><SelectTrigger data-testid="account-project" className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="text-xs">{t("Plattform")}</Label>
                  <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}><SelectTrigger data-testid="account-platform" className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(PLATFORM_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="text-xs">{t("Handle / Name")}</Label><Input data-testid="account-handle" className="mt-1" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} placeholder="@handle" /></div>
              </div>
              <DialogFooter><Button data-testid="account-create-btn" onClick={create} className="rounded-full bg-[#0A0A0A]">{t("Anlegen")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )} />

      {providers && (
        <div className="mb-5 border border-slate-200 rounded-md p-4 bg-slate-50" data-testid="provider-status">
          <div className="flex items-center gap-2 mb-3">
            <Link2 size={15} className="text-slate-500" />
            <span className="text-sm font-semibold">{t("Plattform-Verbindungen (OAuth)")}</span>
            {!providers.posting_enabled && (
              <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 rounded-full px-2 py-0.5" data-testid="posting-disabled-badge">
                <ShieldAlert size={12} /> {t("Echtes Posten deaktiviert")}
              </span>
            )}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {providers.providers.map((p) => (
              <div key={p.key} data-testid={`provider-${p.key}`} className="bg-white border border-slate-200 rounded-md px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{p.label}</span>
                  {p.connectable
                    ? <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 size={13} /> {t(CREDENTIAL_LABEL[p.credential_source] || "Konfiguriert")}</span>
                    : <span className="text-xs text-slate-400 flex items-center gap-1"><AlertCircle size={13} /> {t("Fehlt")}</span>}
                </div>
                {canConnect(p.key) && (
                  <div className="flex items-center gap-2 mt-2">
                    {p.connectable && <Button size="sm" className="h-7 rounded-full bg-[#0A70B7] hover:bg-[#075985] text-xs flex-1" data-testid={`provider-connect-${p.key}`} onClick={() => oauthConnectProvider(p.key)}>{t("Verbinden")}</Button>}
                    {can("admin") && <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" data-testid={`provider-creds-${p.key}`} onClick={() => openCreds(p.key)}>Credentials</Button>}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-xs text-slate-500 mt-3"><b>Comrocket Standard</b> ist der zentrale Agenturweg für Mandanten. Eine <b>mandanteneigene App</b> wird nur verwendet, wenn sie technisch oder vertraglich ausdrücklich erforderlich ist. OAuth-Tokens und Jobs bleiben trotzdem strikt dem ausgewählten Mandanten und Account zugeordnet.</div>
        </div>
      )}

      {connections.length > 0 && (
        <div className="mb-5 border border-slate-200 rounded-md p-4" data-testid="active-connections">
          <div className="text-sm font-semibold mb-3">{t("Aktive Verbindungen")}</div>
          <div className="space-y-2">
            {connections.map((c) => (
              <div key={c.platform} data-testid={`connection-${c.platform}`} className="flex items-center justify-between flex-wrap gap-2 text-sm border-b border-slate-100 last:border-0 pb-2 last:pb-0">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-emerald-600" />
                  <span className="font-medium capitalize">{c.platform}</span>
                  {(c.selected_page_name || c.display_name) && (
                    <span className="text-slate-500">· {c.selected_page_name || c.display_name}</span>
                  )}
                  <span className="inline-flex text-[11px] text-slate-600 bg-slate-100 rounded-full px-2 py-0.5">
                    {CREDENTIAL_LABEL[c.credential_source] || "Verbindungsquelle unbekannt"}
                  </span>
                  {c.platform === "tiktok" && !c.display_name && (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                      <AlertCircle size={12} /> Profil fehlt – TikTok neu verbinden
                    </span>
                  )}
                  {c.access_token_expires_at && (
                    <span className={`text-xs ${tokenExpired(c.access_token_expires_at) ? "text-rose-600 font-medium" : "text-slate-400"}`}>
                      · {tokenExpired(c.access_token_expires_at) ? "abgelaufen" : "läuft ab"} {new Date(c.access_token_expires_at).toLocaleDateString("de-DE")}
                    </span>
                  )}
                  {["facebook", "instagram", "linkedin"].includes(c.platform) && !(c.pages?.length > 0) && (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 rounded-full px-2 py-0.5" data-testid={`connection-nopage-${c.platform}`}>
                      <AlertCircle size={12} /> {c.pages_note || "Keine Seite freigegeben – neu verbinden & Seite anhaken"}
                    </span>
                  )}
                  {["facebook", "instagram", "linkedin"].includes(c.platform) && c.pages?.length > 0 && c.selected_page_name && (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5" data-testid={`connection-page-${c.platform}`}>
                      <CheckCircle2 size={12} /> {t("Seite")}: {c.selected_page_name}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {c.display_name && projects.length > 0 && (
                    <Select disabled={!canConnect(c.platform)} value={c.project_id || ""} onValueChange={(v) => assignConnection(v, c.platform)}>
                      <SelectTrigger data-testid={`${c.platform}-project-select`} className="h-8 text-xs w-64"><SelectValue placeholder={`${PLATFORM_LABEL[c.platform] || c.platform} einem Projekt zuordnen`} /></SelectTrigger>
                      <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  {["facebook", "instagram", "linkedin"].includes(c.platform) && c.pages?.length > 0 && (
                    <Select disabled={!canConnect(c.platform)} value={c.selected_page_id || ""} onValueChange={(v) => selectPage(v, c.platform)}>
                      <SelectTrigger data-testid="meta-page-select" className="h-8 text-xs w-64"><SelectValue placeholder={t("Unternehmensseite wählen")} /></SelectTrigger>
                      <SelectContent>{c.pages.map((pg) => <SelectItem key={pg.id} value={pg.id}>{pg.name}{pg.ig_user_id ? " (+ Instagram)" : ""}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  {can("admin") && <Button size="sm" variant="outline" className="h-8 rounded-full text-xs text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`connection-disconnect-${c.platform}`} onClick={() => disconnectConnection(c.platform)}>{t("Trennen")}</Button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {visibleAccounts.length === 0 ? (
        <EmptyState icon={Plug} title={t("Keine Accounts")} hint={t("Füge TikTok, Instagram, LinkedIn, Facebook oder YouTube Accounts hinzu.")} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleAccounts.map((a) => {
            const effectiveTokenStatus = tokenExpired(a.token_expires_at) ? "abgelaufen" : a.token_status;
            const ts = TOKEN_STYLE[effectiveTokenStatus] || TOKEN_STYLE.nicht_verbunden;
            const TIcon = ts.icon;
            return (
              <Card key={a.id} data-testid={`account-card-${a.id}`} className="p-5 rounded-md border-slate-200 shadow-none">
                <div className="flex items-center justify-between mb-3">
                  <PlatformBadge platform={a.platform} />
                  <span className={`text-xs font-medium flex items-center gap-1 ${ts.cls}`}><TIcon size={13} />{t(ts.label)}</span>
                </div>
                <div className="font-mono font-semibold text-sm">{a.handle}</div>
                <div className={`text-xs mt-0.5 ${effectiveTokenStatus === "abgelaufen" ? "text-rose-600" : "text-slate-400"}`}>
                  Health: {a.health}
                  {a.token_expires_at ? ` · ${effectiveTokenStatus === "abgelaufen" ? "abgelaufen" : "läuft ab"} ${new Date(a.token_expires_at).toLocaleDateString(locale)}` : ""}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">{CREDENTIAL_LABEL[a.credential_source] || "Credential-Quelle wird beim nächsten OAuth gesetzt"}</div>
                <div className="mt-4 flex items-center gap-2">
                  <Select value={a.mode} onValueChange={(v) => setMode(a.id, v)} disabled={Boolean(user?.review_access)}>
                    <SelectTrigger data-testid={`account-mode-${a.id}`} className="h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="simulation">{t("Simulation")}</SelectItem><SelectItem value="assisted">{t("Assistiert")}</SelectItem><SelectItem value="api">{a.platform === "tiktok" ? "TikTok API · Direct Post" : t("API-Automatik")}</SelectItem></SelectContent>
                  </Select>
                  {canConnect(a.platform) && (
                    providerFor(a.platform)?.connectable
                      ? <Button size="sm" className="h-8 rounded-full bg-[#0A0A0A]" data-testid={`account-oauth-${a.id}`} onClick={() => oauthConnect(a.platform, a.id)}>{t("OAuth verbinden")}</Button>
                      : !a.connected && <Button size="sm" variant="outline" className="h-8 rounded-full" data-testid={`account-connect-${a.id}`} onClick={() => connect(a.id)}>{t("Verbinden (sim.)")}</Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!credsDialog} onOpenChange={(o) => !o && setCredsDialog(null)}>
        <DialogContent data-testid="creds-dialog">
          <DialogHeader><DialogTitle className="font-display">{(credsDialog || "").toUpperCase()} Credentials · {current.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Client ID / App ID</Label><Input data-testid="creds-client-id" name={`scc-${credsDialog || "provider"}-client-id`} autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" className="mt-1 font-mono" value={credsForm.app_client_id} onChange={(e) => setCredsForm({ ...credsForm, app_client_id: e.target.value })} placeholder="z.B. 78szhcu637q6mp" /></div>
            <div><Label className="text-xs">Client Secret / App Secret</Label><Input data-testid="creds-client-secret" name={`scc-${credsDialog || "provider"}-client-secret`} autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" type="password" className="mt-1 font-mono" value={credsForm.app_client_secret} onChange={(e) => setCredsForm({ ...credsForm, app_client_secret: e.target.value })} placeholder="••••••••" /></div>
            <p className="text-xs text-slate-500">Wird verschlüsselt gespeichert und schaltet diesen Mandanten bewusst von <b>Comrocket Standard</b> auf eine eigene Plattform-App um. {savedCreds[credsDialog]?.secret_configured ? "Secret ist gespeichert; leer lassen behält es unverändert." : "Ohne eigene Werte wird automatisch Comrocket Standard verwendet."}</p>
          </div>
          <DialogFooter><Button data-testid="creds-save-btn" onClick={saveCreds} className="rounded-full bg-[#0A0A0A]">{t("Speichern")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
